import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import type { CreateProfileRequest } from '../services/profileService';
import {
  SHARED_CALCULATOR_EXAMPLES,
  buildLoadedProfileHistoryLabel,
  type SharedCalculatorHistoryEntry,
  type SharedCalculatorProfile,
} from '../hooks/useSharedProfileHistory';
import {
  useLinkedCalculator,
  requestPendingHistoryLoad,
  CLEAR_SELECTION_VALUE,
  type LinkedCalculatorKey,
} from '../context/LinkedCalculatorContext';
import { useProfile } from '../context/ProfileContext';
import { logCalculatorInteraction, type SuperCalculatorKey } from '../services/analytics';

// Which linked calculator hosts this toolbar — stored in the history entry so
// Profile Manager knows which calculator button to offer on the profile card.
const CALC_KEY_BY_ANALYTICS: Partial<Record<SuperCalculatorKey, LinkedCalculatorKey>> = {
  buying_calculator: 'buying',
  selling_calculator: 'selling',
  affordability: 'maximizer',
  decoupling_calculator: 'decoupling',
};

// How long the save progress bar sweeps — saving always displays for at least
// this long so the user sees the system saving.
const SAVE_FEEDBACK_MS = 1000;

/** What "Save to Profile" should create when no profile is loaded. One member per
 *  borrower on the calculator; two or more members are wrapped in a group. */
export interface NewProfileDraft {
  members: CreateProfileRequest[];
  /** Suggested group name — only used when there is more than one member. */
  groupName?: string;
  /** 'SPOUSE' | 'INDIVIDUAL' — the group's relationship. */
  relationship?: string;
}

type SaveMode = 'history' | 'profile' | 'both';

interface HistoryToolbarProps {
  /** Called when an example key is selected — receives the key and profiles. */
  onExampleLoad?: (exampleKey: string, profiles: readonly SharedCalculatorProfile[]) => void;
  /** Called after a history entry has been loaded (state already distributed via context). */
  onHistoryLoad?: (entry: SharedCalculatorHistoryEntry) => void;
  /** Called when selection is cleared. */
  onClear?: () => void;
  /** Called before saving — should return custom label or undefined. */
  onBeforeSave?: () => string | undefined;
  /** Writes the current inputs back to the loaded profile. With a profile loaded,
   *  the save button becomes "Save to History and Profile" and calls this after
   *  the history save. Returns whether the profile update succeeded. */
  onSaveToProfile?: () => Promise<boolean>;
  /** Builds the profile(s) to create when the user picks "Save to Profile" with no
   *  profile loaded. Providing it turns the save button into a chooser
   *  (history only / profile only / both). Return null when there is nothing to save. */
  onBuildNewProfiles?: () => NewProfileDraft | null;
  /** Runs around profile creation + selection so the host's profile-load effect can
   *  stand aside — otherwise selecting the new profile re-seeds the form. */
  onBeforeProfileCreate?: () => void;
  onAfterProfileCreate?: () => void;
  /** Extra buttons to show after Save to History (e.g. Export PDF). */
  extraButtons?: React.ReactNode;
  /** Which calculator hosts this toolbar — used for Firebase interaction events. */
  analyticsCalculator?: SuperCalculatorKey;
}

export default function HistoryToolbar({
  onExampleLoad,
  onHistoryLoad,
  onClear,
  onBeforeSave,
  onSaveToProfile,
  onBuildNewProfiles,
  onBeforeProfileCreate,
  onAfterProfileCreate,
  extraButtons,
  analyticsCalculator,
}: HistoryToolbarProps) {
  const {
    historyEntries,
    activeSelection,
    selectedExample,
    setSelectedExample,
    setActiveSelection,
    editingHistoryId,
    editingHistoryLabel,
    setEditingHistoryLabel,
    startEditingLabel,
    cancelEditing,
    updateHistoryLabel,
    deleteHistory,
    saveAllToHistory,
    getLastHistoryError,
    loadAllFromHistory,
    showToast,
    historyToast,
    clearToast,
  } = useLinkedCalculator();

  const {
    selectedProfile, getSelectedName, profiles, groups,
    selectProfile, selectGroup, clearSelection, addProfile, addGroup,
  } = useProfile();

  const [loadSelectionOpen, setLoadSelectionOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);

  // ── "Save to History / Profile" chooser (only when no profile is loaded) ──
  const [saveMenuAnchor, setSaveMenuAnchor] = useState<HTMLElement | null>(null);
  const [profileDialogMode, setProfileDialogMode] = useState<SaveMode | null>(null);
  const [draftMembers, setDraftMembers] = useState<CreateProfileRequest[]>([]);
  const [draftNames, setDraftNames] = useState<string[]>([]);
  const [draftGroupName, setDraftGroupName] = useState('');
  const [draftRelationship, setDraftRelationship] = useState('INDIVIDUAL');
  const offersProfileChoice = !selectedProfile && !!onBuildNewProfiles;

  const displayedSelectionValue =
    selectedExample ||
    (activeSelection ? activeSelection.id : '');

  const handleExampleChange = (value: string) => {
    if (value.startsWith('history-')) {
      handleLoadFromHistory(value);
      return;
    }
    if (value === CLEAR_SELECTION_VALUE || !value) {
      setSelectedExample('');
      setActiveSelection(null);
      if (analyticsCalculator) logCalculatorInteraction(analyticsCalculator, 'clear');
      onClear?.();
      return;
    }
    setSelectedExample(value);
    if (value && value in SHARED_CALCULATOR_EXAMPLES) {
      const example = SHARED_CALCULATOR_EXAMPLES[value as keyof typeof SHARED_CALCULATOR_EXAMPLES];
      // Preset examples are never tied to a profile — detach a loaded one.
      if (selectedProfile) clearSelection();
      if (analyticsCalculator) logCalculatorInteraction(analyticsCalculator, 'example_load', { example: value });
      onExampleLoad?.(value, example.profiles);
    }
  };

  const handleLoadFromHistory = (historyId: string) => {
    const entry = historyEntries.find((e) => e.id === historyId);
    if (!entry) return;

    // Keep the loaded profile in step with the entry: switch to the profile the
    // entry was saved with, or detach when the entry isn't tied to any profile.
    const ref = entry.purchasePlannerState?.savedProfileRef;
    const refExists = ref
      ? ref.type === 'group'
        ? groups.some((g) => g.Id === ref.id)
        : profiles.some((p) => p.Id === ref.id)
      : false;
    const refAlreadySelected =
      !!ref && !!selectedProfile && selectedProfile.type === ref.type && selectedProfile.id === ref.id;

    if (ref && refExists && !refAlreadySelected) {
      // Queue the snapshot before switching profile so the calculators'
      // profile-population effects and the provider's auto-restore stand aside;
      // the provider then applies exactly this entry and sets the selection.
      requestPendingHistoryLoad(historyId);
      if (ref.type === 'group') selectGroup(ref.id);
      else selectProfile(ref.id);
      if (analyticsCalculator) logCalculatorInteraction(analyticsCalculator, 'history_load');
      return;
    }

    if (!refAlreadySelected && selectedProfile) {
      // Entry not tied to a (still existing) profile — detach the loaded one.
      clearSelection();
    }

    const loaded = loadAllFromHistory(historyId);
    if (loaded) {
      setSelectedExample(historyId);
      setActiveSelection({
        id: historyId,
        source: 'history',
        profiles: loaded.profiles,
        propertyType: loaded.propertyType,
        purchasePlannerState: loaded.purchasePlannerState,
      });
      if (analyticsCalculator) logCalculatorInteraction(analyticsCalculator, 'history_load');
      onHistoryLoad?.(loaded);
    }
  };

  const reportHistoryFailure = (what: string) => {
    const reason = getLastHistoryError();
    showToast(reason ? `${what} failed — ${reason}` : `${what} failed — please try again`, 'error');
  };

  const handleSave = async (
    createdRef?: { type: 'profile' | 'group'; id: number },
    createdName?: string,
  ) => {
    if (saving) return;
    // Default entry name: with a profile loaded, `<profile name> - <datetime>`
    // (or `<example> - <datetime>` for a preset example); the host calculator's
    // onBeforeSave can still override it.
    // `getSelectedName()` is still empty for a profile created moments ago, so its
    // name is passed in alongside the ref.
    const customLabel =
      onBeforeSave?.() ?? buildLoadedProfileHistoryLabel(createdName ?? getSelectedName(), activeSelection);
    // With a profile loaded, tag the entry with the profile + host calculator so
    // Profile Manager can offer to reload this saved state from the profile card.
    // A profile created moments ago wins over `selectedProfile`, which has not
    // re-rendered with the new selection yet.
    const savedProfileRef = createdRef ?? (selectedProfile
      ? { type: selectedProfile.type, id: selectedProfile.id }
      : undefined);

    setSaving(true);
    setSaveProgress(0);
    const startedAt = Date.now();
    const progressTimer = window.setInterval(() => {
      setSaveProgress(Math.min(100, ((Date.now() - startedAt) / SAVE_FEEDBACK_MS) * 100));
    }, 80);

    try {
      const work = (async () => {
        const created = await saveAllToHistory(customLabel, {
          savedFromCalculator: analyticsCalculator ? CALC_KEY_BY_ANALYTICS[analyticsCalculator] : undefined,
          savedProfileRef,
        });
        // With a profile loaded, the same click also writes the inputs back to it.
        // A just-created profile already holds these values — nothing to write back.
        let profileSaved: boolean | null = null;
        if (created && savedProfileRef && !createdRef && onSaveToProfile) {
          profileSaved = await onSaveToProfile().catch((error) => {
            console.error('Failed to save inputs to profile:', error);
            return false;
          });
        }
        return { created, profileSaved };
      })();

      // Hold for the progress bar's full sweep so the save is visible.
      const [{ created, profileSaved }] = await Promise.all([
        work,
        new Promise((resolve) => window.setTimeout(resolve, SAVE_FEEDBACK_MS)),
      ]);

      if (created) {
        if (analyticsCalculator) logCalculatorInteraction(analyticsCalculator, 'history_save');
        if (createdRef) showToast('Saved to history & profile');
        else if (!savedProfileRef) showToast('Saved to history');
        else if (profileSaved === null) showToast('Saved to history & linked to profile');
        else if (profileSaved) showToast('Saved to history & profile');
        else showToast('Saved to history — profile update failed');
      } else {
        // The save did not happen. Say so — a silent no-op here is
        // indistinguishable from a successful save and the entry simply never
        // appears in the dropdown.
        reportHistoryFailure('Save');
      }
    } finally {
      window.clearInterval(progressTimer);
      setSaving(false);
      setSaveProgress(0);
    }
  };

  // ── Save to Profile (no profile loaded) ──────────────────────────────────
  // Opens the naming dialog seeded from the calculator's current borrowers.
  const openProfileDialog = (mode: SaveMode) => {
    const draft = onBuildNewProfiles?.();
    if (!draft || draft.members.length === 0) {
      showToast('Nothing to save as a profile yet');
      return;
    }
    setDraftMembers(draft.members);
    setDraftNames(draft.members.map((m, i) => (m.Name || '').trim() || `Borrower ${i + 1}`));
    setDraftGroupName(draft.groupName?.trim() || '');
    setDraftRelationship(draft.relationship || 'INDIVIDUAL');
    setProfileDialogMode(mode);
  };

  const closeProfileDialog = () => setProfileDialogMode(null);

  // One member → a single profile; two or more → a profile each, wrapped in a group.
  // The new profile/group is then selected, so the calculator is "on" it afterwards.
  const createProfilesFromDraft = async (): Promise<{ type: 'profile' | 'group'; id: number } | null> => {
    const names = draftMembers.map((m, i) => (draftNames[i] || '').trim() || `Borrower ${i + 1}`);
    onBeforeProfileCreate?.();
    try {
      const created = [];
      for (let i = 0; i < draftMembers.length; i++) {
        created.push(await addProfile({ ...draftMembers[i], Name: names[i] }));
      }
      if (created.length === 1) {
        selectProfile(created[0].Id);
        return { type: 'profile', id: created[0].Id };
      }
      const group = await addGroup({
        Name: draftGroupName.trim() || names.join(' & '),
        Relationship: draftRelationship,
        ProfileIds: created.map((p) => p.Id),
      });
      selectGroup(group.Id);
      return { type: 'group', id: group.Id };
    } catch (error) {
      console.error('Failed to create profile:', error);
      return null;
    } finally {
      onAfterProfileCreate?.();
    }
  };

  const handleProfileDialogConfirm = async () => {
    const mode = profileDialogMode;
    if (!mode || saving) return;
    const savedName = draftMembers.length > 1
      ? (draftGroupName.trim() || draftNames.map((n) => n.trim()).filter(Boolean).join(' & '))
      : (draftNames[0] || '').trim();
    setSaving(true);
    let ref: { type: 'profile' | 'group'; id: number } | null = null;
    try {
      ref = await createProfilesFromDraft();
    } finally {
      setSaving(false);
    }
    closeProfileDialog();
    if (!ref) {
      showToast('Failed to save profile');
      return;
    }
    if (analyticsCalculator) logCalculatorInteraction(analyticsCalculator, 'profile_save');
    if (mode === 'both') {
      await handleSave(ref, savedName);
      return;
    }
    showToast(draftMembers.length > 1 ? 'Profile group saved' : 'Saved to profile');
  };

  const commitHistoryRename = async (entryId: string, label: string) => {
    const ok = await updateHistoryLabel(entryId, label);
    if (!ok) {
      reportHistoryFailure('Rename');
      return;
    }
    showToast('History entry updated');
  };

  const handleDeleteHistory = async (historyId: string) => {
    const ok = await deleteHistory(historyId);
    if (!ok) {
      reportHistoryFailure('Delete');
      return;
    }
    showToast('History entry deleted');
    if (selectedExample === historyId) {
      setSelectedExample('');
    }
  };

  return (
    <>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel shrink>Load Example / History</InputLabel>
          <Select
            displayEmpty
            open={loadSelectionOpen}
            onOpen={() => setLoadSelectionOpen(true)}
            onClose={() => setLoadSelectionOpen(false)}
            value={displayedSelectionValue}
            label="Load Example / History"
            onChange={(e) => {
              handleExampleChange(e.target.value);
              setLoadSelectionOpen(false);
            }}
            sx={{ minWidth: 300 }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Select example or history…
                  </Typography>
                );
              }
              if (value in SHARED_CALCULATOR_EXAMPLES) {
                return SHARED_CALCULATOR_EXAMPLES[value as keyof typeof SHARED_CALCULATOR_EXAMPLES].name;
              }
              const entry = historyEntries.find(e => e.id === value);
              return entry ? entry.label : value;
            }}
          >
            <MenuItem
              value={CLEAR_SELECTION_VALUE}
              sx={{
                my: 0.75,
                mx: 1,
                py: 1.25,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'grey.100',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              Clear Selection
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            {Object.entries(SHARED_CALCULATOR_EXAMPLES).map(([key, ex]) => (
              <MenuItem key={key} value={key}>{ex.name}</MenuItem>
            ))}
            {historyEntries.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
                  History
                </Typography>
                {historyEntries.map((entry) => (
                  <MenuItem
                    key={entry.id}
                    value={entry.id}
                    onClick={(e) => {
                      if (editingHistoryId === entry.id) {
                        e.stopPropagation();
                        return;
                      }
                      handleLoadFromHistory(entry.id);
                      setLoadSelectionOpen(false);
                    }}
                    sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                  >
                    {editingHistoryId === entry.id ? (
                      <TextField
                        value={editingHistoryLabel}
                        onChange={(e) => setEditingHistoryLabel(e.target.value)}
                        onBlur={() => {
                          if (editingHistoryLabel.trim()) {
                            void commitHistoryRename(entry.id, editingHistoryLabel.trim());
                          } else {
                            cancelEditing();
                          }
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            if (editingHistoryLabel.trim()) {
                              void commitHistoryRename(entry.id, editingHistoryLabel.trim());
                            } else {
                              cancelEditing();
                            }
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        autoFocus
                        size="small"
                        fullWidth
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {entry.label}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            startEditingLabel(entry.id);
                          }}
                          sx={{
                            opacity: 0.7,
                            '&:hover': { opacity: 1, backgroundColor: 'action.hover' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            void handleDeleteHistory(entry.id);
                          }}
                          sx={{
                            opacity: 0.7,
                            '&:hover': { opacity: 1, backgroundColor: 'action.hover' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </MenuItem>
                ))}
              </>
            )}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            endIcon={offersProfileChoice ? <ArrowDropDownIcon /> : undefined}
            onClick={(e) => {
              if (offersProfileChoice) setSaveMenuAnchor(e.currentTarget);
              else void handleSave();
            }}
            disabled={saving}
          >
            {selectedProfile
              ? 'Save to History and Profile'
              : offersProfileChoice ? 'Save to History / Profile' : 'Save to History'}
          </Button>
          {saving && (
            <LinearProgress
              variant={saveProgress >= 100 ? 'indeterminate' : 'determinate'}
              value={Math.min(saveProgress, 100)}
              sx={{ height: 4, borderRadius: 2 }}
            />
          )}
        </Box>

        {extraButtons}
      </Box>

      {/* Save chooser — only when nothing is loaded, so the target is ambiguous */}
      <Menu
        anchorEl={saveMenuAnchor}
        open={!!saveMenuAnchor}
        onClose={() => setSaveMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={() => { setSaveMenuAnchor(null); void handleSave(); }}
        >
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Save to History only" secondary="Reopen this case later" />
        </MenuItem>
        <MenuItem
          onClick={() => { setSaveMenuAnchor(null); openProfileDialog('profile'); }}
        >
          <ListItemIcon><PersonAddAlt1Icon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Save to Profile" secondary="Create a client profile from these inputs" />
        </MenuItem>
        <MenuItem
          onClick={() => { setSaveMenuAnchor(null); openProfileDialog('both'); }}
        >
          <ListItemIcon><LibraryAddCheckIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Save to History and Profile" secondary="Both, linked together" />
        </MenuItem>
      </Menu>

      {/* Name the profile(s) about to be created */}
      <Dialog open={profileDialogMode !== null} onClose={closeProfileDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {draftMembers.length > 1 ? 'Save as Profile Group' : 'Save as Profile'}
        </DialogTitle>
        <DialogContent dividers>
          {draftMembers.length > 1 && (
            <>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Group name"
                value={draftGroupName}
                onChange={(e) => setDraftGroupName(e.target.value)}
                placeholder={draftNames.join(' & ')}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                Relationship
              </Typography>
              <RadioGroup
                row
                value={draftRelationship}
                onChange={(e) => setDraftRelationship(e.target.value)}
                sx={{ mb: 1 }}
              >
                <FormControlLabel value="INDIVIDUAL" control={<Radio size="small" />} label={<Typography variant="body2">Individual</Typography>} />
                <FormControlLabel value="SPOUSE" control={<Radio size="small" />} label={<Typography variant="body2">Married Couple</Typography>} />
              </RadioGroup>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {draftMembers.map((member, i) => (
            <Box key={i} sx={{ mb: i < draftMembers.length - 1 ? 2 : 0 }}>
              <TextField
                autoFocus={i === 0 && draftMembers.length === 1}
                fullWidth
                size="small"
                label={draftMembers.length > 1 ? `Borrower ${i + 1} name` : 'Profile name'}
                value={draftNames[i] ?? ''}
                onChange={(e) => setDraftNames((prev) => prev.map((n, idx) => (idx === i ? e.target.value : n)))}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {[
                  member.MonthlyIncome ? `Income $${Math.round(member.MonthlyIncome).toLocaleString('en-SG')}/mo` : null,
                  member.Saving != null ? `Cash $${Math.round(member.Saving).toLocaleString('en-SG')}` : null,
                  member.Cpf != null ? `CPF $${Math.round(member.Cpf).toLocaleString('en-SG')}` : null,
                ].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          ))}

          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
            {profileDialogMode === 'both'
              ? 'The profile is created, loaded, and this case is saved to history against it.'
              : 'The profile is created and loaded, so later saves update it.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={closeProfileDialog} size="small" disabled={saving}>Cancel</Button>
          <Button
            onClick={() => void handleProfileDialogConfirm()}
            size="small"
            variant="contained"
            disabled={saving || draftNames.every((n) => !n.trim())}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast feedback */}
      <Snackbar
        open={historyToast.open}
        autoHideDuration={historyToast.severity === 'error' ? 8000 : 2500}
        onClose={clearToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clearToast}
          severity={historyToast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {historyToast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
